import { Avatar } from '@mui/material';
import dayjs from 'dayjs';
import { get } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { stringAvatar } from 'shared-utils';
import { getCompanyById } from 'src/apis/company';
import { registerNewPosition } from 'src/apis/position';
import CompanyAccountManagers from 'src/components/position/CompanyAccountManagers';
import CreatePosition from 'src/components/position/Create';
import CreatingPositionLoader from 'src/components/position/CreatingPositionLoader';
import SuccessPage from 'src/components/position/preview/SuccessPage';
import { useRecommendationsContext } from 'src/context/RecommendationsProvider';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import { useGetMyCompaniesQuery } from 'src/hooks/company.hooks';
import { useGetPositionsForRecruiterQuery } from 'src/hooks/position.hooks';
import { generateCompanyLogoAvatarSrc } from 'src/utils/companyLogo';
import { MyCompanyType, PositionStatus } from 'types';

function CreatePositionRouter() {
	const { companyId } = useParams();
	const queryClient = useQueryClient();
	const toast = useToast();
	const navigate = useNavigate();
	const { isAccountManagerLoggedIn, user } = useUserContext();
	const { selectedCompany, setSelectedCompany } = useRecommendationsContext();

	const [avatarImg, setAvatarImg] = useState('');
	const [formStep, setFormStep] = useState<number>(0);

	const { data: positionsData } = useGetPositionsForRecruiterQuery(isAccountManagerLoggedIn);

	const getCompanyByIdFn = useMutation(['getCompanyById'], getCompanyById, {
		onSuccess: async data => {
			setSelectedCompany(data as unknown as MyCompanyType);
		},
		onError(error: Error) {
			toast({
				title: `Error occured while fetching company by id ${error.message}`,
				type: 'error',
			});
		},
	});

	const generateAvatar = useCallback(async () => {
		if (user?.registeredCompany?.logo) {
			const res = await generateCompanyLogoAvatarSrc(
				user?.registeredCompany?.logo ?? '',
				user?.registeredCompany?.url ?? '',
			);
			setAvatarImg(res);
		} else if (selectedCompany?.company) {
			const res = await generateCompanyLogoAvatarSrc(
				selectedCompany?.company?.logo ?? '',
				selectedCompany?.company?.url ?? '',
			);
			setAvatarImg(res);
		} else {
			companyId && getCompanyByIdFn.mutate(companyId);
		}
	}, [selectedCompany?.company, user?.registeredCompany?.logo, companyId, getCompanyByIdFn]);

	useEffect(() => {
		generateAvatar();
	}, [
		selectedCompany.company?.logo,
		selectedCompany.company?.url,
		user?.registeredCompany?.logo,
		user?.registeredCompany?.url,
	]);

	const [disableCreate, setDisableCreate] = useState<boolean>(false);
	const [showSuccessPage, setShowSuccessPage] = useState<boolean>(false);

	const registerNewPositionFn = useMutation(['registerNewPosition'], registerNewPosition, {
		onSuccess(data) {
			if (!data.isActionPending && data.position?.company) {
				queryClient.invalidateQueries(['getMyRegisteredPositions', data.position?.company]);
				if (isAccountManagerLoggedIn) navigate(`/job-preview/${data.position?.company}/${data.position?._id}`);
				else navigate(`/job-preview/${data.position?._id}`);
			} else setShowSuccessPage(true);
			localStorage.removeItem('saved-position-data');
		},
		onError(error: Error) {
			setFormStep(0);
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				title: errorMessage,
				duration: 5000,
				type: 'error',
			});
		},
	});

	useEffect(() => {
		return () => {
			localStorage.removeItem('saved-position-data');
		};
	}, []);

	useEffect(() => {
		if (positionsData?.positions?.length) {
			setDisableCreate(
				!isAccountManagerLoggedIn &&
					!!positionsData?.positions?.find(
						p =>
							!p?.position?.isPublished &&
							p.position.positionStatus === PositionStatus.OPEN &&
							dayjs().isBefore(p.position.expiresAt),
					),
			);
		} else {
			setDisableCreate(false);
		}

		if (selectedCompany?.company?.isBlacklisted) {
			setDisableCreate(true);
		}
	}, [isAccountManagerLoggedIn, positionsData, selectedCompany]);

	useEffect(() => {
		disableCreate && navigate('/');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [disableCreate]);

	if (registerNewPositionFn.isLoading) return <CreatingPositionLoader />;
	if (showSuccessPage) return <SuccessPage />;

	return (
		<div className="flex gap-[48px] justify-center">
			<div className="md:py-6 max-w-[580px] w-full">
				<div className="max-w-[580px] mx-auto dark:bg-[#111E2C] bg-[#fff] p-4 md:p-6 md:rounded-[12px] dark:border-0">
					<div className="mb-4 bg-[#FAFAFC] flex justify-center items-center text-center min-w-[64px] min-h-[64px] max-w-[64px] max-h-[64px] rounded-full border border-[#F3F2F2]">
						<Avatar
							alt="companyLogo"
							src={avatarImg}
							sx={{ width: '51.2px', height: '51.2px', minWidth: '51.2px', minHeight: '51.2px', color: 'white' }}
						>
							{stringAvatar(selectedCompany?.company?.name.trim().toUpperCase() || user?.registeredCompany?.name || '')}
						</Avatar>
					</div>

					<h1 className="text-xl md:text-2xl font-bold font-inter dark:text-white text-[#28293D] mb-2">
						Create New Position
					</h1>
					<div className="dark:text-[#E5FFFF] text-[#004FC4] text-base font-inter">
						{isAccountManagerLoggedIn && formStep === 1
							? 'Provide Hiring Partner details.'
							: isAccountManagerLoggedIn
							  ? selectedCompany?.company?.name || user?.registeredCompany?.name
									? `Publish your job posting for ${
											selectedCompany?.company?.name ||
											user?.registeredCompany?.name ||
											localStorage.getItem('companyName') ||
											''
									  }.`
									: 'Publish your job posting'
							  : 'Publish your first job posting and get personalised recommendation in minutes.'}
					</div>
					<CreatePosition
						registerFn={registerNewPositionFn}
						companyId={companyId}
						formStep={formStep}
						setFormStep={setFormStep}
						accountManagers={positionsData?.accountManagers}
					/>
				</div>
			</div>

			{isAccountManagerLoggedIn ? null : (
				<div className="sticky top-6 hidden md:block rounded-[12px] bg-white h-fit px-3 mt-6 w-full max-w-[360px]">
					<CompanyAccountManagers accountManagers={positionsData?.accountManagers} />
				</div>
			)}
		</div>
	);
}

export default CreatePositionRouter;
